import React, { useEffect, useMemo, useState } from 'react';
import {
    Drawer,
    Box,
    Typography,
    Button,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    TextField,
    Checkbox,
    IconButton,
    Link,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { IProduto } from '../../interfaces/IProduto';
import { ICategoria } from '../../interfaces/ICategoria';
import { ImagemContainer } from './styles';
import { CardUploadImagem } from '../../../core/components/CardUploadImagem';
import { MIMETypes } from '../../../core/enum/MIMETypes';
import { useSnackbar } from '../../../core/contexts/SnackbarContext';
import { IImagemProduto } from '../../interfaces/IImagemProduto';
import { v4 } from 'uuid';
import { ItemAtualizarImagem, useProdutos } from '../../hooks/useProdutos';
import { IComplemento } from '../../interfaces/IComplemento';

interface AtualizarImagensDrawerProps {
    handleCancelar: () => void;
    open: boolean;
    categorias: ICategoria[];
    handleRefresh: () => void;
}

const gerarUuidItem = (item: {
    tipo: string;
    produtoUuid?: string;
    itemUuid?: string;
    complementoUuid?: string;
}) => {
    return `${item.tipo}-${item.produtoUuid || ''}-${item.complementoUuid || ''}-${item.itemUuid || ''}`;
};


export const AtualizarImagensDrawer = ({ handleCancelar, open, categorias, handleRefresh }: AtualizarImagensDrawerProps) => {
    const [imagem, setImagem] = useState<undefined | File>(undefined);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [filtroDescricao, setFiltroDescricao] = useState('');
    const [selecionados, setSelecionados] = useState<string[]>([]);
    const { atualizarImagensEmMassa, loading } = useProdutos();

    const handleCancelarDrawer = () => {
        if (loading) return;
        setImagem(undefined);
        setPage(0);
        setRowsPerPage(25);
        setFiltroDescricao('');
        setSelecionados([]);
        handleCancelar();
    }

    const notificar = useSnackbar();

    const mimetypesValidos = [
        MIMETypes["image/jpeg"],
        MIMETypes["image/jpg"],
        MIMETypes["image/png"]
    ] as string[];

    const handleImagemChange = (arquivo: File) => {
        if (!mimetypesValidos.includes(arquivo.type)) {
            return notificar(`Arquivo inválido, tipos aceitos: (PNG, JPEG)`, { severity: "error" });
        }
        setImagem(arquivo);
    };

    const handleRemoveImagem = () => setImagem(undefined);

    const itens: ItemAtualizarImagem[] = useMemo(() => {
        if(!open) return [];

        const lista: ItemAtualizarImagem[] = [];
        categorias?.forEach(categoria => {
            categoria?.produtos?.forEach(produto => {
                lista.push({
                    descricao: produto.descricao,
                    tipo: 'Produto',
                    uuid: gerarUuidItem({ tipo: 'Produto', produtoUuid: produto.uuid }),
                    categoriaUuid: categoria.uuid,
                    produtoUuid: produto.uuid,
                    imagens: produto.imagens,
                    itemUuid: produto.uuid
                });

                produto?.sabores?.forEach(sabor => {
                    lista.push({
                        descricao: sabor.descricao,
                        tipo: 'Sabor',
                        uuid: gerarUuidItem({ tipo: 'Sabor', produtoUuid: produto.uuid, itemUuid: sabor.uuid }),
                        itemUuid: sabor.uuid,
                        categoriaUuid: categoria.uuid,
                        produtoUuid: produto.uuid,
                        imagens: sabor.imagens
                    });
                });

                produto?.bordas?.forEach(borda => {
                    lista.push({
                        uuid: gerarUuidItem({ tipo: 'Borda', produtoUuid: produto.uuid, itemUuid: borda.uuid }),
                        descricao: borda.descricao,
                        tipo: 'Borda',
                        itemUuid: borda.uuid,
                        categoriaUuid: categoria.uuid,
                        produtoUuid: produto.uuid,
                        imagens: borda.imagens
                    });
                });

                produto?.massas?.forEach(massa => {
                    lista.push({
                        uuid: gerarUuidItem({ tipo: 'Massa', produtoUuid: produto.uuid, itemUuid: massa.uuid }),
                        descricao: massa.descricao,
                        tipo: 'Massa',
                        itemUuid: massa.uuid,
                        categoriaUuid: categoria.uuid,
                        produtoUuid: produto.uuid,
                        imagens: massa.imagens
                    });
                });

                produto?.complementos?.forEach(complemento => {
                    complemento?.itens?.forEach(item => {
                        lista.push({
                            uuid: gerarUuidItem({ tipo: 'Item', produtoUuid: produto.uuid, itemUuid: item.uuid, complementoUuid: complemento.uuid }),
                            descricao: item.descricao,
                            tipo: 'Item',
                            itemUuid: item.uuid,
                            categoriaUuid: categoria.uuid,
                            produtoUuid: produto.uuid,
                            complementoUuid: complemento.uuid,
                            imagens: item.imagens
                        });
                    });
                });
            });
        });
        return lista;
    }, [categorias, open]);

    const itensFiltrados = useMemo(() => {
        return itens.filter(item =>
            item.descricao.toLowerCase().includes(filtroDescricao.toLowerCase())
        );
    }, [itens, filtroDescricao]);

    const itensPaginados = useMemo(() => {
        const start = page * rowsPerPage;
        const end = start + rowsPerPage;
        return itensFiltrados.slice(start, end);
    }, [itensFiltrados, page, rowsPerPage]);

    const handleAtualizar = async () => {
        if (!imagem) return notificar("Imagem inválida", { severity: "error" });
        if(!itens) return notificar("Nenhum item válido selecionado", { severity: "error" });

        const itensSelecionados = itens?.filter(item => selecionados.includes(item.uuid));
        if(!itensSelecionados?.length) return notificar("Nenhum item selecionado", { severity: "error" });
        
        await atualizarImagensEmMassa(imagem, itensSelecionados);
        handleRefresh();
        handleCancelarDrawer();
    };


    const handleSelectItem = (uuid: string) => {
        setSelecionados(prev =>
            prev.includes(uuid)
                ? prev.filter(id => id !== uuid)
                : [...prev, uuid]
        );
    };

    return (
        <Drawer anchor="right" open={open} onClose={handleCancelarDrawer} sx={{ '& .MuiDrawer-paper': { width: '800px', padding: '1rem' } }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Atualizar Imagens</Typography>
                <IconButton onClick={handleCancelarDrawer}><CloseIcon /></IconButton>
            </Box>

            <ImagemContainer>
                <CardUploadImagem
                    titulo="Imagem"
                    descricao={imagem ? imagem.name : "Faça o upload"}
                    url={imagem ? URL.createObjectURL(imagem) : undefined}
                    excluir={handleRemoveImagem}
                    upload={handleImagemChange}
                />
            </ImagemContainer>

            <Box sx={{ paddingY: "1rem" }}>
                <TextField
                    label="Filtrar por descrição"
                    fullWidth
                    value={filtroDescricao}
                    onChange={e => setFiltroDescricao(e.target.value)}
                />
            </Box>

            <TableContainer component={Box}>
                <Table size="medium">
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell>Imagem</TableCell>
                            <TableCell>Descrição</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Categoria</TableCell>
                            <TableCell>Produto</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {itensPaginados.map((item) => {
                            const categoria = categorias?.find(categoria => categoria.uuid == item.categoriaUuid);
                            const produto = categoria?.produtos?.find(produto => produto.uuid == item.produtoUuid);

                            return (
                                <TableRow key={item.uuid}>
                                    <TableCell>
                                        <Checkbox
                                            checked={selecionados.includes(item.uuid)}
                                            onChange={() => handleSelectItem(item.uuid)}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <img alt={item.descricao} src={
                                            item?.imagens?.length
                                                ? item?.imagens[0]?.url
                                                : `${process.env.PUBLIC_URL}/images/produtos/generico.png`
                                        } style={{
                                            width: "60px",
                                            height: "50px",
                                            borderRadius: "5px"
                                        }} />
                                    </TableCell>
                                    <TableCell>{item.descricao}</TableCell>
                                    <TableCell>{item.tipo}</TableCell>
                                    <TableCell>
                                        {categoria
                                            ? <Link target='_blank' href={`/cardapio/categorias/${categoria.uuid}`}>{categoria?.descricao}</Link>
                                            : 'Não encontrado'
                                        }
                                    </TableCell>
                                    <TableCell>
                                        {produto && categoria
                                            ? <Link target='_blank' href={`/cardapio/categorias/${categoria.uuid}/produtos/${produto.uuid}`}>{produto?.descricao}</Link>
                                            : 'Não encontrado'
                                        }
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={itensFiltrados.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    labelRowsPerPage="Linhas por página"
                />
            </TableContainer>
            <Box display="flex" justifyContent="flex-end" alignItems={"center"}  gap={2} mt={2}>
                <Button variant='outlined' disabled={loading} onClick={handleCancelarDrawer}>Cancelar</Button>
                {loading ? (
                    <CircularProgress size={25} />
                ) : (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAtualizar}
                        disabled={selecionados.length === 0 || !imagem}
                    >
                        Atualizar
                    </Button>
                )}
            </Box >
        </Drawer >
    );
};
